'use client';
import { DynamicIcon } from '@curology/ui-components-web/react/Icon/DynamicIcon';
import { AgencyCheckmarkIcon } from '@curology/ui-components-web/react/Icon/icons/AgencyCheckmarkIcon';
import { CheckmarkIcon } from '@curology/ui-components-web/react/Icon/icons/CheckmarkIcon';
import { Typography } from '@curology/ui-components-web/react/Typography';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import React from 'react';
import { type ItemValue, type QuestionRadiogroupModel } from 'survey-core';

import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { getBailout } from '@/components/flow/steps/survey/surveyjsSurvey/surveyjs/utils/bailOutHandler';
import { type Brand } from '@/consts/brands';
import { useRouter } from '@/hooks/useRouter';

import { autoAdvancingAtom } from '../../atoms/autoAdvancingAtom';

// This global state is necessary for state to be shared between radio items of a radio group
// It is included here because rendering each radio item has been abstracted away by SurveyJS
let arrowPressed = false;

const getBrandStyles = (brand: Brand) => {
  switch (brand) {
    case 'agency':
      return {
        checkmarkIcon: <AgencyCheckmarkIcon size="sm" />,
        selectedClasses:
          'cur-bg-surface-highlight cur-ring-4 cur-ring-surface-secondary',
        unselectedClasses:
          'cur-bg-surface-primary cur-ring-2 cur-ring-surface-disabled',
      };
    case 'curology':
    default:
      return {
        checkmarkIcon: (
          <CheckmarkIcon className="cur-text-surface-brand-accent" size="sm" />
        ),
        selectedClasses:
          'cur-bg-surface-highlight cur-ring-4 cur-ring-surface-brand',
        unselectedClasses:
          'cur-bg-surface-neutral cur-ring-4 cur-ring-transparent',
      };
  }
};

const RadioItem = ({
  question,
  item,
  isChecked,
}: {
  question: QuestionRadiogroupModel;
  item: ItemValue;
  isChecked: boolean;
}) => {
  const { brand, treatmentCategory } = useFlowContext();

  const brandStyles = getBrandStyles(brand);

  const router = useRouter();
  const [autoAdvancing, setIsAutoAdvancing] = useAtom(autoAdvancingAtom);
  const handleOnChange = () => {
    if (autoAdvancing) {
      return;
    }

    question.clickItemHandler(item);
    if (!arrowPressed) {
      if (item.selectionText) {
        return;
      }

      const timeout =
        question.surveyModel.getPropertyValue('autoAdvanceDelay') || 250;
      setIsAutoAdvancing(true);

      setTimeout(async () => {
        const bailoutRoute = await getBailout(
          treatmentCategory ?? 'skin',
          question,
          brand,
        );

        if (bailoutRoute) {
          router.push(bailoutRoute);
        } else {
          question.surveyModel.nextPage() ||
            question.surveyModel.completeLastPage();
        }
        setIsAutoAdvancing(false);
      }, timeout);
    }
    arrowPressed = false;
  };

  // @ts-expect-error - TODO: broken by noImplicitAny
  const handleKeyDownChange = (event) => {
    if (event.key.includes('Arrow')) {
      arrowPressed = true;
    }
  };

  return (
    <div>
      <div role="presentation" className="cur-text-primary">
        <input
          role="option"
          aria-selected={isChecked}
          className="cur-peer cur-sr-only"
          id={question.getItemId(item)}
          type="radio"
          tabIndex={0}
          name={question.questionName}
          defaultChecked={isChecked}
          value={item.value}
          onChange={handleOnChange}
          onKeyDown={handleKeyDownChange}
          aria-label={item.type}
        />
        <label
          htmlFor={question.getItemId(item)}
          className={clsx(
            'cur-flex cur-relative cur-items-center cur-pl-4 cur-cursor-pointer peer-focus-visible:cur-ring-offset-border-brand-selected peer-focus-visible:cur-ring-offset-2',
            item.description ? 'cur-py-5 cur-pr-12' : 'cur-py-6 cur-pr-6',
            isChecked
              ? brandStyles.selectedClasses
              : brandStyles.unselectedClasses,
          )}
        >
          {item.inputIcon && (
            <DynamicIcon
              name={item.inputIcon}
              size="lg"
              className="cur-mr-3 cur-flex-shrink-0"
            />
          )}
          <div>
            <Typography size="body" weight="bold">
              {item.text}
            </Typography>
            {item.description && (
              <Typography size="meta">{item.description}</Typography>
            )}
          </div>
          {isChecked && (
            <div className="cur-absolute cur-flex cur-items-center cur-right-0 cur-mr-6">
              {brandStyles.checkmarkIcon}
            </div>
          )}
        </label>
      </div>
      {isChecked && item.selectionText && (
        <Typography size="meta" className="cur-pt-2 cur-text-highlight">
          {item.selectionText}
        </Typography>
      )}
    </div>
  );
};

export default RadioItem;
